import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { CheckCircle2, FlaskConical, GitPullRequestArrow, Plus, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';
import {
  activateEligibilityRuleSet,
  addDraftEligibilityRule,
  apiErrorMessage,
  createRuleSetDraft,
  getRuleWorkbench,
  proposeEligibilityRuleSet,
  removeDraftEligibilityRule,
  reviewEligibilityRuleSet,
  simulateEligibilityRuleSet,
  updateDraftEligibilityRule,
} from '../api/abfApi';
import { ActionLocalFeedback, type ActionFeedbackState } from '../components/ActionFeedback';
import { PrerequisiteAction } from '../components/PrerequisiteAction';
import { getTenantToday } from '../../../utils/dateUtils';
import { formatMoney } from '../../../utils/amountUtils';
import { Button, Card, CardHead, Eyebrow, FieldLabel, INK, Input, Muted } from '../onboarding/review/reviewStyles';
import { Note } from '../onboarding/review/contractReviewStyles';
import { amendmentReviewPath } from '../routes';
import type {
  EligibilityRuleUpdate,
  GovernedEligibilityRule,
  RuleSimulationRun,
  RuleWorkbenchReadModel,
} from '../types/collateralExplorer';
import { DateField } from '../../../components/shared/DateField';

interface Props {
  facilityId: number;
  tapeIngestId: number;
  userRole?: string;
  onFeedback: (feedback: ActionFeedbackState | null) => void;
}

/* ------------------------------------------------------------------ review-standard styling */

const Facts = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 8px; margin-top: 12px;
  > div { min-width: 0; padding: .6rem .7rem; border: 1px solid ${INK.line}; border-radius: 9px; background: ${INK.raised}; }
  strong { display: block; margin-top: 3px; color: ${INK.strong}; font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const RulesTable = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 8px;
  th, td { padding: 7px 8px; text-align: left; border-bottom: 1px solid ${INK.line}; vertical-align: top; }
  th { color: ${INK.muted}; font-size: 11px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; }
  td { color: ${INK.body}; }
  td input, td select { ${'' /* compact inline editors */} min-height: 32px; font-size: 12px; }
  td.source { color: ${INK.faint}; font-size: 12px; max-width: 26ch; overflow-wrap: anywhere; }
  @media (max-width: 900px) { display: block; overflow-x: auto; }
`;

const Section = styled.div`
  margin-top: 12px; padding: .8rem; border: 1px solid ${INK.line}; border-radius: 10px; background: ${INK.raised};
  h3 { margin: 0 0 6px; color: ${INK.strong}; font-size: 13px; }
`;

const Select = styled.select`
  box-sizing: border-box; width: 100%; min-width: 0; padding: .5rem .6rem; border: 1px solid ${INK.lineStrong}; border-radius: 8px;
  background: ${INK.well}; color: ${INK.body}; color-scheme: dark; font: inherit; font-size: .76rem;
  option { background: #0f172a; }
`;

const TextArea = styled.textarea`
  box-sizing: border-box; width: 100%; min-height: 60px; padding: .5rem .6rem; border: 1px solid ${INK.lineStrong}; border-radius: 8px;
  background: ${INK.well}; color: ${INK.body}; font: inherit; font-size: .76rem; resize: vertical;
`;

const Grid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr)); gap: 9px; align-items: end;
`;

/* ------------------------------------------------------------------ vocabulary */

const FAMILIES = [
  { value: 'ROW', label: 'Loan test (required field)' },
  { value: 'ROW_OPTIONAL', label: 'Loan test (skip when the field is missing)' },
  { value: 'ADVANCE_RATE', label: 'Advance rate' },
  { value: 'CONCENTRATION_OBLIGOR', label: 'Single obligor cap' },
  { value: 'CONCENTRATION_STATE', label: 'Single state cap' },
  { value: 'CONCENTRATION_VINTAGE', label: 'Vintage cap' },
  { value: 'CONCENTRATION_COUNTRY', label: 'Country cap' },
];
// The backend's operator set (ABFEligibilityRuleSemantics): NE, not NEQ; IN / NOT_IN take a comma-separated list.
const OPERATORS = ['LTE', 'GTE', 'LT', 'GT', 'EQ', 'NE', 'IN', 'NOT_IN'];
const SEVERITIES = ['INELIGIBLE', 'WARNING'];

const signedMoney = (amount: unknown) => typeof amount === 'number' ? (amount >= 0 ? '+' : '−') + formatMoney(Math.abs(amount)) : '—';
const display = (value: unknown) => value === null || value === undefined || value === '' ? 'Not supplied' : String(value).replace(/_/g, ' ');
const familyLabel = (value: string) => FAMILIES.find(family => family.value === value)?.label || display(value);
const roleAliases: Record<string, string> = { PORTFOLIO_MGR: 'PORTFOLIO_MANAGER', RISK_MGR: 'RISK_MANAGER', COMPLIANCE_OFFICER: 'COMPLIANCE' };
const makerRoles = new Set(['PM', 'PORTFOLIO_MANAGER', 'TRADER', 'OPERATIONS', 'OPERATIONS_ANALYST', 'MIDDLE_OFFICE_ADMIN', 'ADMIN', 'PLATFORM_ADMIN']);
const checkerRoles = new Set(['RISK', 'RISK_MANAGER', 'COMPLIANCE', 'MIDDLE_OFFICE_ADMIN', 'ADMIN', 'PLATFORM_ADMIN']);
const fmtDate = (value: string) => new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

const emptyNewRule = () => ({ ruleName: '', ruleType: 'ROW', fieldName: '', operator: 'LTE', thresholdValue: '', thresholdText: '', advanceRatePct: '', severity: 'INELIGIBLE', sourceClause: '' });

const RuleWorkbench: React.FC<Props> = ({ facilityId, tapeIngestId, userRole, onFeedback }) => {
  const [model, setModel] = useState<RuleWorkbenchReadModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState(getTenantToday());
  const [proposalReason, setProposalReason] = useState('Update eligibility criteria with clause-backed evidence.');
  const [reviewNotes, setReviewNotes] = useState('Reviewed against source clauses and simulation evidence.');
  const [activationNotes, setActivationNotes] = useState('Activate approved eligibility rule set.');
  const [localFeedback, setLocalFeedback] = useState<ActionFeedbackState | null>(null);
  const [latestSimulation, setLatestSimulation] = useState<RuleSimulationRun | null>(null);
  const [scheduledFor, setScheduledFor] = useState<string | null>(null);
  const [draftRefusal, setDraftRefusal] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newRule, setNewRule] = useState(emptyNewRule());
  const [removing, setRemoving] = useState<{ id: number; name: string; reason: string } | null>(null);

  const normalizedRole = roleAliases[String(userRole || '').toUpperCase()] || String(userRole || '').toUpperCase();
  const canMake = model?.prerequisites.canMake ?? makerRoles.has(normalizedRole);
  const canCheck = model?.prerequisites.canCheck ?? checkerRoles.has(normalizedRole);

  const notify = useCallback((feedback: ActionFeedbackState) => { setLocalFeedback(feedback); onFeedback(feedback); }, [onFeedback]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await getRuleWorkbench(facilityId, tapeIngestId);
      setModel(next);
      setLatestSimulation(next.recentSimulations[0] || null);
    } catch (error) {
      notify({ tone: 'error', message: apiErrorMessage(error, 'Rule Workbench could not be loaded.') });
    } finally { setLoading(false); }
  }, [facilityId, notify, tapeIngestId]);

  useEffect(() => { void load(); }, [load]);

  const act = async (key: string, operation: () => Promise<unknown>, success: string) => {
    setBusy(key);
    try {
      await operation();
      notify({ tone: 'success', message: success });
      await load();
    } catch (error) {
      const message = apiErrorMessage(error, 'The governed rule action could not be completed.');
      if (key === 'draft') setDraftRefusal(message);
      notify({ tone: 'error', message });
    } finally { setBusy(null); }
  };

  const proposal = model?.proposal;
  const simulationMatches = Boolean(proposal && latestSimulation
    && (latestSimulation.definition_hash || latestSimulation.definitionHash) === proposal.definition_hash);
  const rules = useMemo(() => proposal?.rules || model?.activeRuleSet?.rules || [], [model?.activeRuleSet?.rules, proposal?.rules]);
  const editable = proposal?.state === 'DRAFT' && canMake;
  const latestImpact = latestSimulation?.impact;
  const duplicate = rules.find(rule => rule.rule_name.trim().toLowerCase() === newRule.ruleName.trim().toLowerCase());
  const stagedAmendment = draftRefusal ? /version id (\d+)/i.exec(draftRefusal) : null;

  if (loading && !model) return <Card style={{ marginTop: '.85rem' }}><Muted>Loading governed rule authority…</Muted></Card>;
  if (!model) return null;

  const submitNewRule = () => {
    if (!proposal) return;
    const numberOrNull = (value: string) => value.trim() === '' ? null : Number(value);
    void act('add', () => addDraftEligibilityRule(facilityId, proposal.id, {
      ruleName: newRule.ruleName.trim(), ruleType: newRule.ruleType, fieldName: newRule.fieldName.trim() || null,
      operator: newRule.operator, thresholdValue: numberOrNull(newRule.thresholdValue), thresholdText: newRule.thresholdText.trim() || null,
      advanceRatePct: numberOrNull(newRule.advanceRatePct), severity: newRule.severity, priority: 100, enabled: true,
      sourceReference: null, sourceClause: newRule.sourceClause.trim() || null, sourcePage: null, sourceSection: null,
    }), `${newRule.ruleName.trim()} added to the draft; effective authority is unchanged.`).then(() => { setAdding(false); setNewRule(emptyNewRule()); });
  };

  return (
    <Card style={{ marginTop: '.85rem' }} aria-label="Eligibility Rule Workbench">
      <CardHead>
        <div><h2>Eligibility Rule Workbench</h2><p>A tightening is your policy, not a contract change: draft, simulate, have it reviewed independently, activate. It shares the contract's four-eyes rule.</p></div>
        <Button type="button" disabled={loading} onClick={() => void load()}><RefreshCw size={14} /> Refresh</Button>
      </CardHead>

      <Facts>
        <div><Eyebrow>Effective authority</Eyebrow><strong>{model.activeRuleSet ? `Rule set version ${model.activeRuleSet.version_no}` : 'None'}</strong></div>
        <div><Eyebrow>Proposal</Eyebrow><strong>{proposal ? `Version ${proposal.version_no} · ${display(proposal.state).toLowerCase()}` : 'None open'}</strong></div>
        <div><Eyebrow>Contract lineage</Eyebrow><strong>Facility version {proposal?.facility_version_no || model.activeRuleSet?.facility_version_no || '—'}</strong></div>
        <div><Eyebrow>Simulation tape</Eyebrow><strong>{fmtDate(model.selectedTape.as_of_date)} · #{model.selectedTape.id}</strong></div>
      </Facts>

      {scheduledFor && <Note style={{ marginTop: 12 }}><strong>Approved — takes effect on {fmtDate(scheduledFor)}</strong><p>The morning activation job activates it that day; the current set stays in force until then.</p></Note>}

      {!proposal && (
        <Section>
          <h3>Propose a tightening</h3>
          <Grid>
            <FieldLabel>Proposed effective date<DateField min={getTenantToday()} minLabel="today" value={effectiveFrom} onChange={setEffectiveFrom} /></FieldLabel>
            <PrerequisiteAction primary disabled={!canMake || busy !== null || !model.activeRuleSet}
              disabledReason={!canMake ? 'A portfolio, trading, operations, or administrator maker role is required.' : !model.activeRuleSet ? 'An active governed rule set is required.' : model.prerequisites.createDraft}
              onClick={() => { setDraftRefusal(null); void act('draft', () => createRuleSetDraft(facilityId, effectiveFrom), 'Draft rule set created; the active set is unchanged.'); }}
            ><GitPullRequestArrow size={14} /> {busy === 'draft' ? 'Creating…' : 'Create a draft from the active set'}</PrerequisiteAction>
          </Grid>
          {draftRefusal && <Note role="alert" style={{ marginTop: 10 }}>
            <p>{draftRefusal}</p>
            {stagedAmendment && <p><Link to={amendmentReviewPath(facilityId, stagedAmendment[1])} style={{ color: INK.accentSoft }}>Open the amendment review →</Link></p>}
          </Note>}
        </Section>
      )}

      {proposal && (
        <Facts>
          <div><Eyebrow>State</Eyebrow><strong>{display(proposal.state).toLowerCase()}</strong></div>
          <div><Eyebrow>Effective from</Eyebrow><strong>{fmtDate(proposal.effective_from)}</strong></div>
          <div><Eyebrow>Proposed by</Eyebrow><strong>{display(proposal.proposed_by)}</strong></div>
          <div><Eyebrow>Reviewed by</Eyebrow><strong>{display(proposal.reviewed_by)}</strong></div>
        </Facts>
      )}

      <Section>
        <h3>{proposal ? `Draft rules (version ${proposal.version_no})` : `Active rules (version ${model.activeRuleSet?.version_no ?? '—'})`}</h3>
        <RulesTable>
          <thead><tr><th>Rule</th><th>Family</th><th>Field</th><th>Test</th><th>Advance %</th><th>Severity</th><th>Source</th>{editable && <th />}</tr></thead>
          <tbody>
            {rules.map(rule => <RuleRow key={rule.id + ':' + (proposal?.definition_hash || 'active')} rule={rule} editable={editable} saving={busy === 'rule-' + rule.id}
              onSave={request => proposal && act('rule-' + rule.id, () => updateDraftEligibilityRule(facilityId, proposal.id, rule.id, request), `${rule.rule_name} saved in the draft.`)}
              onRemove={() => setRemoving({ id: rule.id, name: rule.rule_name, reason: '' })} />)}
          </tbody>
        </RulesTable>
        {removing && proposal && <Note role="dialog" aria-label="Remove a rule" style={{ marginTop: 10 }}>
          <strong>Remove "{removing.name}" from the draft</strong>
          <p>The rule stays on the active set until this draft is activated. Say why it goes; the reason is recorded with the proposal.</p>
          <Input value={removing.reason} placeholder="Why this rule is removed" onChange={event => setRemoving({ ...removing, reason: event.target.value })} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Button $variant="danger" type="button" disabled={busy !== null || !removing.reason.trim()}
              onClick={() => void act('remove', () => removeDraftEligibilityRule(facilityId, proposal.id, removing.id, removing.reason.trim()), `${removing.name} removed from the draft.`).then(() => setRemoving(null))}>
              <Trash2 size={13} /> {busy === 'remove' ? 'Removing…' : 'Remove'}
            </Button>
            <Button $variant="quiet" type="button" onClick={() => setRemoving(null)}>Keep it</Button>
          </div>
        </Note>}
        {editable && !adding && <div style={{ marginTop: 10 }}><Button type="button" onClick={() => setAdding(true)}><Plus size={13} /> Add a rule</Button></div>}
        {editable && adding && proposal && <Note style={{ marginTop: 10 }} aria-label="Add a rule">
          <strong>Add a rule to the draft</strong>
          <Grid style={{ marginTop: 8 }}>
            <FieldLabel>Name<Input value={newRule.ruleName} onChange={event => setNewRule({ ...newRule, ruleName: event.target.value })} placeholder="e.g. Maximum loan balance" /></FieldLabel>
            <FieldLabel>Family<Select value={newRule.ruleType} onChange={event => setNewRule({ ...newRule, ruleType: event.target.value })}>{FAMILIES.map(family => <option key={family.value} value={family.value}>{family.label}</option>)}</Select></FieldLabel>
            <FieldLabel>Tape field<Input value={newRule.fieldName} onChange={event => setNewRule({ ...newRule, fieldName: event.target.value })} placeholder="e.g. current_balance" /></FieldLabel>
            <FieldLabel>Operator<Select value={newRule.operator} onChange={event => setNewRule({ ...newRule, operator: event.target.value })}>{OPERATORS.map(op => <option key={op} value={op}>{op.replace('_', ' ')}</option>)}</Select></FieldLabel>
            <FieldLabel>{newRule.ruleType.startsWith('CONCENTRATION') ? 'Cap (% of eligible pool)' : 'Threshold (number)'}<Input inputMode="decimal" value={newRule.thresholdValue} onChange={event => setNewRule({ ...newRule, thresholdValue: event.target.value })} /></FieldLabel>
            <FieldLabel>Threshold (text, if not a number)<Input value={newRule.thresholdText} onChange={event => setNewRule({ ...newRule, thresholdText: event.target.value })} /></FieldLabel>
            {newRule.ruleType === 'ADVANCE_RATE' && <FieldLabel>Advance rate %<Input inputMode="decimal" value={newRule.advanceRatePct} onChange={event => setNewRule({ ...newRule, advanceRatePct: event.target.value })} /></FieldLabel>}
            <FieldLabel>Severity<Select value={newRule.severity} onChange={event => setNewRule({ ...newRule, severity: event.target.value })}>{SEVERITIES.map(item => <option key={item} value={item}>{item.toLowerCase()}</option>)}</Select></FieldLabel>
            <FieldLabel>Source clause<Input value={newRule.sourceClause} onChange={event => setNewRule({ ...newRule, sourceClause: event.target.value })} placeholder="e.g. Section 1.01, Eligible Receivable (k)" /></FieldLabel>
          </Grid>
          {duplicate && newRule.ruleName.trim() && <p style={{ color: '#f8a5a5' }}>A rule named "{duplicate.rule_name}" already exists in this set ({display(duplicate.field_name)} {display(duplicate.operator)} {duplicate.threshold_value ?? duplicate.threshold_text ?? '—'}). Edit that rule or choose a different name.</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <Button $variant="primary" type="button" disabled={busy !== null || Boolean(duplicate) || !newRule.ruleName.trim() || !newRule.fieldName.trim() || (!newRule.thresholdValue.trim() && !newRule.thresholdText.trim()) || !newRule.sourceClause.trim()} onClick={submitNewRule}><Plus size={13} /> {busy === 'add' ? 'Adding…' : 'Add to the draft'}</Button>
            <Button $variant="quiet" type="button" onClick={() => { setAdding(false); setNewRule(emptyNewRule()); }}>Cancel</Button>
          </div>
        </Note>}
      </Section>

      {proposal && (
        <Section>
          <Grid style={{ alignItems: 'start' }}>
            <div>
              <FieldLabel>Proposal rationale<TextArea value={proposalReason} disabled={proposal.state !== 'DRAFT'} onChange={event => setProposalReason(event.target.value)} /></FieldLabel>
              <div style={{ marginTop: 8 }}><PrerequisiteAction primary disabled={!canMake || proposal.state !== 'DRAFT' || busy !== null || !proposalReason.trim()}
                disabledReason={!canMake ? 'A maker role is required to submit a proposal.' : proposal.state !== 'DRAFT' ? 'Only a draft can be proposed.' : !proposalReason.trim() ? 'Enter a proposal rationale.' : undefined}
                onClick={() => void act('propose', () => proposeEligibilityRuleSet(facilityId, proposal.id, proposalReason), 'Rule set submitted for independent review.')}
              ><GitPullRequestArrow size={14} /> {busy === 'propose' ? 'Submitting…' : 'Submit for review'}</PrerequisiteAction></div>
            </div>
            <div>
              <Eyebrow>Released-tape simulation</Eyebrow>
              <p style={{ color: INK.muted, fontSize: 12, lineHeight: 1.45 }}>Compares this candidate with the active set on the selected released tape. Read-only.</p>
              <PrerequisiteAction disabled={busy !== null} disabledReason={model.prerequisites.simulate}
                onClick={() => void act('simulate', async () => { setLatestSimulation(await simulateEligibilityRuleSet(facilityId, proposal.id, tapeIngestId)); }, 'Simulation recorded; effective authority is unchanged.')}
              ><FlaskConical size={14} /> {busy === 'simulate' ? 'Simulating…' : 'Simulate on the selected tape'}</PrerequisiteAction>
            </div>
          </Grid>
        </Section>
      )}

      {latestImpact && (
        <Section style={{ borderColor: simulationMatches ? '#376e60' : '#7a5a1a' }}>
          <h3>Candidate impact</h3>
          <Facts style={{ marginTop: 6 }}>
            <div><Eyebrow>Eligibility gains</Eyebrow><strong>{latestImpact.eligibilityGainedCount} · {formatMoney(latestImpact.eligibilityGainedBalance)}</strong></div>
            <div><Eyebrow>Eligibility losses</Eyebrow><strong>{latestImpact.eligibilityLostCount} · {formatMoney(latestImpact.eligibilityLostBalance)}</strong></div>
            <div><Eyebrow>Eligible balance delta</Eyebrow><strong>{signedMoney(latestImpact.eligibleBalanceDelta)}</strong></div>
            <div><Eyebrow>Borrowing-base delta</Eyebrow><strong style={{ color: latestImpact.borrowingBaseDelta < 0 ? '#f8a5a5' : '#7cddbe' }}>{signedMoney(latestImpact.borrowingBaseDelta)}</strong></div>
          </Facts>
          <p style={{ color: INK.muted, fontSize: 12, marginTop: 8 }}>
            {simulationMatches ? 'Simulation matches the current proposal definition.' : 'The proposal changed after this simulation; simulate again before review.'}
            {' '}Baseline: rule set version {latestSimulation?.baseline_rule_set_version ?? latestSimulation?.baselineRuleSetVersion ?? '—'} for {fmtDate(model.selectedTape.as_of_date)}. Showing {Math.min(latestSimulation?.affectedAssets?.length || 0, 8)} of {latestImpact.affectedAssetCount} affected loans.
          </p>
          {(latestSimulation?.affectedAssets || []).length > 0 && <RulesTable>
            <thead><tr><th>Loan</th><th>Balance</th><th>Eligibility</th></tr></thead>
            <tbody>{(latestSimulation?.affectedAssets || []).slice(0, 8).map(asset => <tr key={asset.assetId}>
              <td>{asset.assetIdentifier}</td><td>{formatMoney(asset.balance)}</td>
              <td style={{ color: asset.candidateEligible ? '#7cddbe' : '#f8a5a5' }}>{asset.baselineEligible ? 'Eligible' : 'Ineligible'} → {asset.candidateEligible ? 'Eligible' : 'Ineligible'}</td>
            </tr>)}</tbody>
          </RulesTable>}
        </Section>
      )}

      {proposal?.state === 'PROPOSED' && (
        <Section>
          <FieldLabel>Independent review notes<TextArea value={reviewNotes} onChange={event => setReviewNotes(event.target.value)} /></FieldLabel>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <PrerequisiteAction primary disabled={!canCheck || !model.prerequisites.reviewAllowed || !simulationMatches || busy !== null || !reviewNotes.trim()}
              disabledReason={!canCheck ? 'A risk, compliance, or administrator checker role is required.' : !model.prerequisites.reviewAllowed ? model.prerequisites.review : !simulationMatches ? 'Run a simulation matching the current definition first.' : !reviewNotes.trim() ? 'Enter independent review notes.' : model.prerequisites.review}
              onClick={() => void act('approve', () => reviewEligibilityRuleSet(facilityId, proposal.id, true, reviewNotes), 'Rule set independently approved; it is not yet effective.')}
            ><CheckCircle2 size={14} /> {busy === 'approve' ? 'Approving…' : 'Approve'}</PrerequisiteAction>
            <PrerequisiteAction disabled={!canCheck || !model.prerequisites.reviewAllowed || busy !== null || !reviewNotes.trim()}
              disabledReason={!canCheck ? 'A checker role is required.' : !model.prerequisites.reviewAllowed ? model.prerequisites.review : !reviewNotes.trim() ? 'Enter rejection notes.' : undefined}
              onClick={() => void act('reject', () => reviewEligibilityRuleSet(facilityId, proposal.id, false, reviewNotes), 'Rule set rejected; effective authority is unchanged.')}
            >Reject proposal</PrerequisiteAction>
          </div>
        </Section>
      )}

      {proposal?.state === 'APPROVED' && (
        <Section style={{ borderColor: '#376e60' }}>
          <FieldLabel>Activation notes<TextArea value={activationNotes} onChange={event => setActivationNotes(event.target.value)} /></FieldLabel>
          <div style={{ marginTop: 8 }}>
            <PrerequisiteAction primary disabled={!canCheck || !model.prerequisites.activationAllowed || !simulationMatches || busy !== null}
              disabledReason={!canCheck ? 'A risk, compliance, or administrator checker role is required.' : !model.prerequisites.activationAllowed ? model.prerequisites.activate : !simulationMatches ? 'Run a simulation matching the approved definition.' : model.prerequisites.activate}
              onClick={() => void act('activate', async () => {
                const result = await activateEligibilityRuleSet(facilityId, proposal.id, activationNotes);
                setScheduledFor(result.scheduledFor || null);
              }, 'Approved rule set processed; existing economic authorities are unchanged.')}
            ><ShieldCheck size={14} /> {busy === 'activate' ? 'Activating…' : proposal.effective_from > getTenantToday() ? `Approve and schedule for ${fmtDate(proposal.effective_from)}` : 'Activate approved rule set'}</PrerequisiteAction>
          </div>
        </Section>
      )}

      <p style={{ marginTop: 10, color: INK.faint, fontSize: 12 }}>{model.authorityBoundary}</p>
      <ActionLocalFeedback feedback={localFeedback} />
    </Card>
  );
};

const RuleRow: React.FC<{
  rule: GovernedEligibilityRule; editable: boolean; saving: boolean;
  onSave: (request: EligibilityRuleUpdate) => void; onRemove: () => void;
}> = ({ rule, editable, saving, onSave, onRemove }) => {
  const [draft, setDraft] = useState({
    thresholdValue: rule.threshold_value === null ? '' : String(rule.threshold_value),
    thresholdText: rule.threshold_text || '',
    advanceRatePct: rule.advance_rate_pct === null ? '' : String(rule.advance_rate_pct),
    sourceClause: rule.source_clause || '',
  });
  const dirty = draft.thresholdValue !== (rule.threshold_value === null ? '' : String(rule.threshold_value))
    || draft.thresholdText !== (rule.threshold_text || '') || draft.advanceRatePct !== (rule.advance_rate_pct === null ? '' : String(rule.advance_rate_pct))
    || draft.sourceClause !== (rule.source_clause || '');
  const numberOrNull = (value: string) => value.trim() === '' ? null : Number(value);
  return <tr>
    <td><strong>{rule.rule_name}</strong><div style={{ color: INK.faint, fontSize: 11 }}>{display(rule.authority_status).toLowerCase()}</div></td>
    <td>{familyLabel(rule.rule_type)}</td>
    <td>{display(rule.field_name)}</td>
    <td>{editable
      ? <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>{display(rule.operator)} <Input aria-label={`${rule.rule_name} threshold`} value={draft.thresholdValue || draft.thresholdText} onChange={event => /^-?[\d.,]*$/.test(event.target.value) ? setDraft({ ...draft, thresholdValue: event.target.value, thresholdText: '' }) : setDraft({ ...draft, thresholdText: event.target.value, thresholdValue: '' })} /></span>
      : <span>{display(rule.operator)} {rule.threshold_value ?? rule.threshold_text ?? '—'}</span>}</td>
    <td>{rule.rule_type === 'ADVANCE_RATE' ? (editable ? <Input aria-label={`${rule.rule_name} advance rate`} value={draft.advanceRatePct} onChange={event => setDraft({ ...draft, advanceRatePct: event.target.value })} /> : (rule.advance_rate_pct ?? '—')) : '—'}</td>
    <td>{display(rule.severity).toLowerCase()}</td>
    <td className="source">{editable ? <Input aria-label={`${rule.rule_name} source clause`} value={draft.sourceClause} onChange={event => setDraft({ ...draft, sourceClause: event.target.value })} /> : (rule.source_clause || rule.source_reference || '—')}</td>
    {editable && <td style={{ whiteSpace: 'nowrap' }}>
      <Button type="button" disabled={saving || !dirty} onClick={() => onSave({
        ruleName: rule.rule_name, ruleType: rule.rule_type, fieldName: rule.field_name, operator: rule.operator,
        thresholdValue: numberOrNull(draft.thresholdValue), thresholdText: draft.thresholdText.trim() || null,
        advanceRatePct: numberOrNull(draft.advanceRatePct), severity: rule.severity, priority: rule.priority, enabled: rule.enabled,
        sourceReference: rule.source_reference || null, sourceClause: draft.sourceClause.trim() || null,
        sourcePage: rule.source_page || null, sourceSection: rule.source_section || null,
      })}>{saving ? 'Saving…' : 'Save'}</Button>
      {' '}<Button $variant="quiet" type="button" onClick={onRemove}><Trash2 size={13} /></Button>
    </td>}
  </tr>;
};

export default RuleWorkbench;
